/**
 * Meta Pixel (Facebook / Instagram Ads) — troisième destination analytics du
 * web, à côté de PostHog (product) et GA4 (attribution Google).
 *
 * Le script `fbevents.js` est chargé par `AnalyticsProvider` (next/script) ;
 * ce module se contente de pousser des events dans `window.fbq`. Le snippet
 * de base définit `fbq` avec une file d'attente interne AVANT que la lib soit
 * téléchargée : les appels faits trop tôt sont donc rejoués, pas perdus.
 *
 * Désactivé tant que `NEXT_PUBLIC_FB_PIXEL_ID` n'est pas renseigné : toutes
 * les fonctions deviennent alors des no-op silencieux.
 *
 * @see https://developers.facebook.com/docs/meta-pixel/reference
 */
import type { GA4Item } from "./google";

export const FB_PIXEL_ID = process.env.NEXT_PUBLIC_FB_PIXEL_ID ?? "";

const FORCE_ENABLE = process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === "true";

/** Activé en production (ou en local si le flag debug est posé) et si un ID existe. */
export const FB_ENABLED =
  !!FB_PIXEL_ID && (process.env.NODE_ENV === "production" || FORCE_ENABLE);

declare global {
  interface Window {
    fbq?: (...args: unknown[]) => void;
    _fbq?: unknown;
  }
}

/** Pousse une commande fbq si le snippet est présent. Silencieux sinon. */
function fbq(...args: unknown[]): void {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  window.fbq(...args);
}

// ─── Mapping événements internes → événements standards Meta ─────────────────

/**
 * Les 17 événements standards du Pixel. Tout ce qui n'est pas dans cette liste
 * (ni traduit par `INTERNAL_TO_META`) part en `trackCustom` : Meta refuse un
 * `track` sur un nom inconnu, il finirait en event ignoré côté Ads Manager.
 * @see https://developers.facebook.com/docs/meta-pixel/reference#standard-events
 */
const FB_STANDARD_EVENTS = new Set([
  "AddPaymentInfo",
  "AddToCart",
  "AddToWishlist",
  "CompleteRegistration",
  "Contact",
  "CustomizeProduct",
  "Donate",
  "FindLocation",
  "InitiateCheckout",
  "Lead",
  "PageView",
  "Purchase",
  "Schedule",
  "Search",
  "StartTrial",
  "SubmitApplication",
  "Subscribe",
  "ViewContent",
]);

/**
 * Nos events sont nommés à la sauce GA4 (snake_case). On traduit ceux qui ont
 * un équivalent standard Meta pour qu'ils alimentent l'optimisation des pubs
 * et les audiences ; le reste passe en événement custom.
 */
const INTERNAL_TO_META: Record<string, string> = {
  page_view: "PageView",
  view_item: "ViewContent",
  add_to_cart: "AddToCart",
  add_to_wishlist: "AddToWishlist",
  begin_checkout: "InitiateCheckout",
  add_payment_info: "AddPaymentInfo",
  purchase: "Purchase",
  order_placed: "Purchase",
  search: "Search",
  sign_up: "CompleteRegistration",
  generate_lead: "Lead",
  contact: "Contact",
};

// ─── Devise : Meta n'accepte pas le dinar algérien ───────────────────────────

/**
 * Meta REJETTE `DZD`. Un `Purchase` envoyé avec cette devise déclenche
 * « Parameter 'currency' is invalid for event 'Purchase' » : la conversion
 * remonte, mais **sans montant** — donc pas de ROAS, et rien sur quoi
 * l'algorithme d'enchères puisse optimiser.
 *
 * On convertit donc vers une devise supportée **pour Meta uniquement**.
 * Google, PostHog et Amplitude acceptent le DZD (code ISO 4217 valide) et
 * continuent de recevoir les montants réels : la donnée produit reste en
 * dinars, seule l'attribution publicitaire est convertie.
 *
 * ⚠️ Le taux est une CONSTANTE, pas un taux du jour. C'est délibéré : Meta s'en
 * sert pour comparer des conversions entre elles, pas pour tenir une
 * comptabilité. Un taux fixe garde les montants comparables dans le temps ; un
 * taux flottant ferait bouger l'historique sans qu'aucune vente n'ait changé.
 */
const META_CURRENCY = "USD";

/** Dinars pour 1 USD. Surchargeable sans redéploiement de code. */
const DZD_PER_USD = Number(process.env.NEXT_PUBLIC_META_DZD_PER_USD ?? 134);

/** Devises transmises telles quelles (aucune conversion nécessaire). */
const META_NATIVE_CURRENCIES = new Set([
  "USD",
  "EUR",
  "GBP",
  "CAD",
  "AUD",
  "CHF",
]);

/** Convertit un montant vers une devise que Meta accepte. */
function toMetaMoney(
  amount: number,
  currency: string
): { value: number; currency: string } {
  const code = currency.toUpperCase();
  if (META_NATIVE_CURRENCIES.has(code)) return { value: amount, currency: code };
  if (code === "DZD" && DZD_PER_USD > 0) {
    // Deux décimales : Meta arrondit de toute façon, et un flottant à quinze
    // chiffres ne rend pas la valeur plus juste.
    return {
      value: Math.round((amount / DZD_PER_USD) * 100) / 100,
      currency: META_CURRENCY,
    };
  }
  // Devise inconnue : transmise telle quelle plutôt que d'inventer un taux.
  // Meta l'ignorera si elle n'est pas supportée — préférable à un montant faux.
  return { value: amount, currency: code };
}

// ─── Normalisation des paramètres ────────────────────────────────────────────

/** Article au format Pixel (`contents`). */
interface FbContent {
  id: string;
  quantity: number;
  item_price?: number;
}

/** Convertit des items GA4 en `contents` Meta. */
function toContents(items: GA4Item[]): FbContent[] {
  return items.map((item) => ({
    id: String(item.item_id ?? item.item_name ?? ""),
    quantity: item.quantity ?? 1,
    ...(typeof item.price === "number" ? { item_price: item.price } : {}),
  }));
}

/**
 * Traduit un payload GA4 générique en paramètres Pixel : `items` devient
 * `contents` + `content_ids`, le reste des scalaires est conservé tel quel
 * (Meta accepte des paramètres custom à côté des siens).
 */
function toPixelParams(
  properties?: Record<string, unknown>
): Record<string, unknown> {
  if (!properties) return {};
  const { items, ...rest } = properties as { items?: GA4Item[] } & Record<
    string,
    unknown
  >;

  const contents =
    Array.isArray(items) && items.length > 0 ? toContents(items) : null;

  const params: Record<string, unknown> = contents
    ? {
        ...rest,
        contents,
        content_ids: contents.map((c) => c.id),
        content_type: "product",
        ...(items!.length === 1 && items![0].item_name
          ? { content_name: items![0].item_name }
          : {}),
      }
    : rest;

  // Conversion de devise EN DERNIER, une fois `contents` construit : elle doit
  // s'appliquer au montant de l'événement ET aux prix unitaires, sans quoi Meta
  // recevrait un total en dollars et des lignes en dinars.
  if (typeof params.currency === "string") {
    const source = params.currency;

    if (typeof params.value === "number") {
      const money = toMetaMoney(params.value, source);
      params.value = money.value;
      params.currency = money.currency;
    } else {
      params.currency = toMetaMoney(0, source).currency;
    }

    if (Array.isArray(params.contents)) {
      params.contents = (params.contents as FbContent[]).map((c) =>
        typeof c.item_price === "number"
          ? { ...c, item_price: toMetaMoney(c.item_price, source).value }
          : c
      );
    }
  }

  return params;
}

/**
 * Envoie l'event. `eventID` (optionnel) sert à dédupliquer avec la Conversions
 * API si elle est branchée plus tard côté serveur : même id des deux côtés =
 * une seule conversion comptée.
 */
function send(
  metaEvent: string,
  params: Record<string, unknown>,
  isStandard: boolean,
  eventID?: string
): void {
  const method = isStandard ? "track" : "trackCustom";
  if (eventID) fbq(method, metaEvent, params, { eventID });
  else fbq(method, metaEvent, params);
}

// ─── Identification (Advanced Matching) ──────────────────────────────────────

/**
 * Indicatif appliqué aux numéros locaux (`0…`) avant hachage : Meta attend un
 * numéro avec indicatif pays, sans `+` ni séparateur.
 */
const DEFAULT_COUNTRY_CODE = "213"; // Algérie

/** Normalise un téléphone pour l'Advanced Matching (chiffres + indicatif). */
function normalizePhone(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("00")) return digits.slice(2);
  if (digits.startsWith("0")) return DEFAULT_COUNTRY_CODE + digits.slice(1);
  return digits;
}

/** Dernières données de matching connues, rejouées quand le snippet est prêt. */
let _userData: Record<string, string> | null = null;

function applyUserData(): void {
  if (!_userData) return;
  // Un second `init` sur le même pixel met à jour les données de matching
  // sans recharger la lib ni ré-émettre de PageView.
  fbq("init", FB_PIXEL_ID, _userData);
}

/**
 * Advanced Matching : ré-initialise le pixel avec les données utilisateur pour
 * améliorer le taux de correspondance des conversions. Le navigateur hache
 * (SHA-256) email/téléphone avant envoi — rien de lisible ne part en clair.
 *
 * @see https://developers.facebook.com/docs/meta-pixel/advanced/advanced-matching
 */
export function setFacebookUserData(user: {
  id?: string;
  phone?: string;
  email?: string;
  name?: string;
}): void {
  if (!FB_ENABLED) return;
  const data: Record<string, string> = {};
  if (user.email) data.em = user.email.trim().toLowerCase();
  if (user.phone) {
    const ph = normalizePhone(user.phone);
    if (ph) data.ph = ph;
  }
  if (user.id) data.external_id = user.id;
  if (user.name) {
    const [first, ...rest] = user.name.trim().toLowerCase().split(/\s+/);
    if (first) data.fn = first;
    if (rest.length) data.ln = rest.join(" ");
  }
  if (Object.keys(data).length === 0) return;
  _userData = data;
  applyUserData();
}

/**
 * Rejoue l'Advanced Matching une fois `fbevents.js` exécuté. L'utilisateur est
 * souvent identifié dès l'hydratation (session restaurée depuis le cookie),
 * donc AVANT que le script `afterInteractive` ait défini `fbq` : sans ce
 * rattrapage, l'identité serait perdue pour toute la session.
 */
export function flushFacebookUserData(): void {
  if (!FB_ENABLED) return;
  applyUserData();
}

/**
 * Logout. Le Pixel n'expose aucune API pour effacer les données d'Advanced
 * Matching en mémoire : elles disparaissent au prochain chargement complet de
 * page. Les cookies `_fbp`/`_fbc` restent anonymes (pas d'identité dedans),
 * donc rien de personnel ne fuit entre deux comptes sur un même onglet.
 */
export function resetFacebookUser(): void {
  // On oublie au moins les données côté app pour ne pas les rejouer.
  _userData = null;
}

// ─── Events génériques ────────────────────────────────────────────────────────

/** Event Pixel générique : `track` si standard (ou traduit), `trackCustom` sinon. */
export function logFacebookEvent(
  eventName: string,
  parameters?: Record<string, unknown>
): void {
  if (!FB_ENABLED) return;
  const { eventID, ...properties } = (parameters ?? {}) as {
    eventID?: string;
  } & Record<string, unknown>;
  const mapped = INTERNAL_TO_META[eventName];
  const isStandard = !!mapped || FB_STANDARD_EVENTS.has(eventName);
  send(mapped ?? eventName, toPixelParams(properties), isStandard, eventID);
}

/**
 * Pageview manuel (SPA). Le tout premier PageView est déjà émis par le snippet
 * de base : ne l'appeler que sur les changements de route suivants, sinon la
 * page d'entrée est comptée deux fois.
 */
export function logFacebookPageView(): void {
  if (!FB_ENABLED) return;
  fbq("track", "PageView");
}

// ─── Events e-commerce (spec Meta) ───────────────────────────────────────────

export function logFacebookViewContent(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!FB_ENABLED) return;
  fbq("track", "ViewContent", toPixelParams(params));
}

export function logFacebookAddToCart(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!FB_ENABLED) return;
  fbq("track", "AddToCart", toPixelParams(params));
}

export function logFacebookAddToWishlist(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!FB_ENABLED) return;
  fbq("track", "AddToWishlist", toPixelParams(params));
}

export function logFacebookInitiateCheckout(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!FB_ENABLED) return;
  const numItems = params.items.reduce((n, i) => n + (i.quantity ?? 1), 0);
  fbq("track", "InitiateCheckout", {
    ...toPixelParams(params),
    num_items: numItems,
  });
}

export function logFacebookAddPaymentInfo(params: {
  currency: string;
  value: number;
  items: GA4Item[];
}): void {
  if (!FB_ENABLED) return;
  fbq("track", "AddPaymentInfo", toPixelParams(params));
}

/**
 * Achat — l'event qui compte pour l'optimisation des campagnes.
 * `eventID` : passer l'id de commande si la Conversions API est branchée, pour
 * dédupliquer navigateur ↔ serveur.
 */
export function logFacebookPurchase(params: {
  currency: string;
  value: number;
  items: GA4Item[];
  eventID?: string;
}): void {
  if (!FB_ENABLED) return;
  const { eventID, ...rest } = params;
  send("Purchase", toPixelParams(rest), true, eventID);
}

export function logFacebookSearch(params: {
  search_string: string;
  items?: GA4Item[];
}): void {
  if (!FB_ENABLED) return;
  fbq("track", "Search", toPixelParams(params));
}

export function logFacebookCompleteRegistration(params?: {
  method?: string;
  currency?: string;
  value?: number;
}): void {
  if (!FB_ENABLED) return;
  fbq("track", "CompleteRegistration", params ?? {});
}

export function logFacebookLead(params?: Record<string, unknown>): void {
  if (!FB_ENABLED) return;
  fbq("track", "Lead", params ?? {});
}
